import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Clock, ArrowRight } from "lucide-react";
import { API_URL } from "../../utils/api";

export default function ConfirmationPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [message, setMessage] = useState("Vérification en cours...");
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");

  useEffect(() => {
    if (!token) return;

    const confirmUser = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/auth/confirm/${token}`);

        setMessage(data.message || "Compte confirmé avec succès !");
        setStatus("success");

        // Redirection après 3 secondes
         setTimeout(() => navigate("/login"), 3000);
      } catch (error: any) {
        setStatus("error");
        console.error(error)
        if (error.response) {
          setMessage(error.response.data.message || "Lien invalide ou expiré");
        } else {
          setMessage("Impossible de contacter le serveur. Réessayez plus tard.");
        }
      }
    };

    confirmUser();
  }, [token, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case "loading":
        return <Clock className="w-16 h-16 text-primary animate-pulse" />;
      case "success":
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case "error":
        return <XCircle className="w-16 h-16 text-red-500" />;
      default:
        return <Clock className="w-16 h-16 text-primary" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "loading":
        return "text-primary";
      case "success":
        return "text-green-600";
      case "error":
        return "text-red-600";
      default:
        return "text-primary";
    }
  };

  const getBackgroundGradient = () => {
    switch (status) {
      case "loading":
        return "from-primary/5 to-primary/10";
      case "success":
        return "from-green-50 to-emerald-50";
      case "error":
        return "from-red-50 to-orange-50";
      default:
        return "from-primary/5 to-primary/10";
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${getBackgroundGradient()}`}>
      <div className="max-w-md w-full mx-4">
        {/* Carte principale */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
          {/* Icône d'état */}
          <div className="flex justify-center mb-6">
            <div className={`p-4 rounded-2xl ${
              status === 'loading' ? 'bg-primary/10' : 
              status === 'success' ? 'bg-green-50' : 
              'bg-red-50'
            }`}>
              {getStatusIcon()}
            </div>
          </div>

          {/* Titre */}
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {status === "loading" && "Confirmation en cours"}
            {status === "success" && "Compte confirmé !"}
            {status === "error" && "Erreur de confirmation"}
          </h1>

          {/* Message */}
          <p className={`text-lg font-medium mb-6 ${getStatusColor()}`}>
            {message}
          </p>

          {/* Détails supplémentaires */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-600">
              {status === "loading" && "Veuillez patienter pendant que nous vérifions votre compte..."}
              {status === "success" && "Votre compte a été activé avec succès. Vous allez être redirigé vers la page de connexion."}
              {status === "error" && "Veuillez vérifier votre lien ou contacter le support si le problème persiste."}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {status === "success" && (
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Redirection automatique...</span>
              </div>
            )}

            {status === "error" && (
              <div className="space-y-3">
                <button
                  onClick={() => navigate("/login")}
                  className="w-full bg-primary text-white py-3 px-4 rounded-xl hover:bg-primary/90 transition-all duration-200 font-medium flex items-center justify-center space-x-2"
                >
                  <span>Aller à la connexion</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => window.location.reload()}
                  className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Réessayer
                </button>
              </div>
            )}

            {status === "loading" && (
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-primary rounded-full h-2 animate-pulse w-3/4"></div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Besoin d'aide ?{" "}
            <a 
              href="mailto:support@hylia-erp.com" 
              className="text-primary hover:text-primary/80 font-medium transition-colors duration-200"
            >
              Contactez le support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}